import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import * as uuidValidate from 'uuid-validate';
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators';
import {
    validatePassengerName,
    validatePassengerNameLast,
    validateDocumentValidTill,
    validateDocumentNumber,
    validateCertificateAge,
    validateCertificateArmenia,
    validateCertificateSymbol,
    validateDocumentForeign,
    validateBirthday,
    validateBirthdayMin,
    validatePhone,
    isCitizenshipAccept,
    isCitizenshipFilled,
    isValidDate,
    onlyOneUniqueCard,
    cardAirlineIdRequiredIfCardNotEmpty,
    validateTrainSelectedPlaces,
    validationPassengersBirthday
} from 'core/validators';
import router from 'src/router';
import { G2S } from 'core/g2s';
import * as auth from 'core/auth';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
import { Filters } from 'core/filters';
import FlightsBookingConfirmationComponent from './confirmation';
import FlightsBookingPassengersComponent from './passengers';
import FlightsBookingPaymentComponent from './payment';
import FlightsBookingSeatsComponent from './seats';
import FlightsBookingServicesComponent from './services';
import FlightsBookingSidebarComponent from './sidebar';
import { YandexPaymentData } from 'core/payment';
import { clearInterval, setInterval } from 'timers';
import { DialogSbpQrComponent } from 'src/components/payment/sbp-qr';
import { ReservationService } from 'core/api';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

type FormStep = 'passengers' | 'seats' | 'services' | 'payment' | 'confirmation';

@WithRender
@Component({
    beforeRouteEnter(to, from, next) {
        window.scrollTo(0, 0);
        if (from.name == 'flightSearch' || from.name == 'flightSearchRoundtrip') {
            to.meta.followWasMadeFromSearchPage = true;
        } else {
            to.meta.followWasMadeFromSearchPage = false;
        }
        next();
    },
    beforeRouteLeave(to, from, next) {
        // Aviasales passenger input hadling
        (<any>window).AviasalesInject.resetPassengers();
        next();
    },
    components: {
        'v-flights-booking-confirmation': FlightsBookingConfirmationComponent,
        'v-flights-booking-passengers': FlightsBookingPassengersComponent,
        'v-flights-booking-payment': FlightsBookingPaymentComponent,
        'v-flights-booking-seats': FlightsBookingSeatsComponent,
        'v-flights-booking-services': FlightsBookingServicesComponent,
        'v-flights-booking-sidebar': FlightsBookingSidebarComponent,
        'v-dialog-sbp-qr': DialogSbpQrComponent
    },
    validations: {
        reservation: {
            contacts: {
                email: { required, email },
                phone: { required, validatePhone }
            },
            //termsAccepted: { validateChecked },
            passengers: {
                validationPassengersBirthday,
                $each: {
                    lastName: { required, validatePassengerName },
                    firstName: { required, validatePassengerName },
                    middleName: { validatePassengerNameLast },
                    citizenship: { required, isCitizenshipAccept, isCitizenshipFilled },
                    documentNumber: { required, validateDocumentNumber, validateCertificateSymbol, validateDocumentForeign, validateCertificateAge, validateCertificateArmenia, minLength: minLength(4), maxLength: maxLength(G2S.config.locale === 'ru' ? 15 : 20) },
                    documentValidTill: { validateDocumentValidTill, isValidDate },
                    gender: { required },
                    birthday: { required, validateBirthday, validateBirthdayMin, isValidDate },
                    card: { onlyOneUniqueCard, maxLength: maxLength(15) },
                    cardAirlineId: { cardAirlineIdRequiredIfCardNotEmpty }
                }
            },
            trains: {
                $each: {
                    selectedPlaces: { validateTrainSelectedPlaces }
                }
            }
        }
    }
})
export default class FlightsBookingComponent extends Vue {
    $auth: auth.AuthService; // Injected in G2S.init
    
    $refs!: {
        dialogSbp: DialogSbpQrComponent;
    }

    @Prop()
    flightId: string;

    @Prop()
    searchId: string;

    @Prop()
    searchFrom: string;

    @Prop()
    searchFrom2: string;

    @Prop()
    searchTo: string;

    @Prop()
    searchTo2: string;

    @Prop()
    searchDate: string;

    @Prop()
    searchDate2: string;

    @Prop()
    searchPassengers: string;

    @Prop()
    searchClass: string;

    @Prop()
    searchFilters: string;

    bookingCompleted: boolean = false;

    currentUser: api.UserWithBonuses | null = null;

    flightBookingDetails: api.FlightBookingDetails | null = null; // NOTE: null forces reactive vue property

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    trackParams: utils.TrackParams = new utils.TrackParams();

    gaAlreadySubmitted: string[] = [];

    brandChangeState: string = '';

    reservation: flights.ReservationModel = new flights.ReservationModel();

    reservationInfo: api.ReservationInfo | null = null;

    paymentInfo: api.PaymentInfo | null = null;
    
    private authToken: string | null = null;

    destinations: api.Destination[] = [];

    disabledServiceButtons: boolean = false;

    selectedFlightAdapter: string = '';
    selectedFlightAirlinesCode: string = '';
    selectedFlightTfid: number | null = null;

    selectedProduct: flights.ProductModel | null = null;

    alreadyInPackageModal = {
        message: '',
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => { },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        refuseCallback: () => { }
    }

    modalProductConfirmTestMode: boolean = false;

    modalUpgradeBaggageContent = {
        isCloseIconVisible: true,
        text: '',
        buttonText1: '',
        buttonText2: '',
        buttonText3: '',
        buttonAction1: '',
        buttonAction2: '',
        buttonAction3: '',
        buttonColor1: '',
        buttonColor2: '',
        buttonColor3: '',
        buttonIcon1: '',
        buttonIcon2: '',
        buttonIcon3: '',
        buttonStyle1: '',
        buttonStyle2: '',
        buttonStyle3: '',
        emTextColor: '',
        kitId: 0,
        productId: 0
    };
    productIdToConfirm: number | null | undefined = null;
    modalConfirmProductContent: api.ProductPopup | null = null;

    view: 'loading' | 'form' | 'details' | 'booking' | 'error' | 'network-error' = 'loading';
    formStep: FormStep = 'passengers';

    lastHistoryStateView: string = '';

    canGoBackOrForwardToPassengers: boolean = false;
    canGoBackOrForwardToPayment: boolean = false;
    canGoBackOrForwardToSeats: boolean = false;
    canGoBackOrForwardToServices: boolean = false;

    anyProductConfirmationWasShown: boolean = false;

    baggageQueryParamAlreadyHandled: boolean = false;

    mountedTimeStampMs: number = 0;

    NO_BAGGAGE_GUID = '00000000-0000-0000-0000-000000000000';

    @Watch('reservation.selectedFlightIdForProducts')
    watchForSelectedFlightIdForProducts() {
        const selectedProductIds = this.reservation.rawProducts.filter(p => p.selected).map(p => p.product.id);
        const productIdsNeedToSelect = this.reservation.rawProducts.filter(p => p.needToSelect).map(p => p.product.id);

        for (const product of this.reservation.rawProducts) {
            product.selected = false;
            product.needToSelect = false;
        }

        for (const product of this.reservation.products) {
            if (selectedProductIds.includes(product.product.id)) {
                product.selected = true;
            }

            if (productIdsNeedToSelect.includes(product.product.id)) {
                product.needToSelect = true;
            }
        }
    }

    @Watch('this.reservation.couponDiscountAmount')
    discountIntervalChecker() {  
        const discountCheck = setInterval(() => {
            this.reservation.getDiscountAmount();
            if(this.reservation.getDiscountAmount() > 0) {
                clearInterval(discountCheck);
            }
        }, 120000);
    }
    
    @Watch('$v.reservation.$model.couponProduct', {deep : true})
    couponCancelChecker(){
        if(this.reservation.couponProductId && this.reservation.couponProduct) {
            if (this.reservation.couponProduct.selected !== true) {
                this.reservation.couponDiscountAmount = 0;
            } else {
                this.reservation.couponDiscountAmount = this.reservation.couponDiscountAmountOld;
            }
        }
    }

    get searchParams() {
        if (this.searchFrom2 && this.searchTo2 && this.searchDate2) {
            return new flights.FlightNavigationService().getRoundtripParams(
                this.searchFrom,
                this.searchTo,
                this.searchDate,
                this.searchFrom2,
                this.searchTo2,
                this.searchDate2,
                this.searchPassengers,
                this.searchClass
            );
        }
        else {
            return new flights.FlightNavigationService().getOnewayParams(
                this.searchFrom,
                this.searchTo,
                this.searchDate,
                this.searchPassengers,
                this.searchClass
            );
        }
    }

    get productToConfirm(): flights.ProductModel | null {
        if (this.productIdToConfirm) {
            return utils.find(this.reservation.products,
                (p: flights.ProductModel) => {
                    let discount = true;
                    if (this.modalConfirmProductContent && this.modalConfirmProductContent.discountId) {
                        discount = this.modalConfirmProductContent.discountId === p.product.discountRuleId;
                    }
                    return p.product.id === this.productIdToConfirm && discount;
                });
        }
        return null;
    }

    get searchResultsUrl(): string {
        return new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!);
    }

    get followWasMadeFromSearchPage(): boolean {
        return router.currentRoute.meta.followWasMadeFromSearchPage;
    }

    get hasSelectedPaidOutwardBaggage(): boolean {
        return this.reservation.hasSelectedOutwardCustomBaggage
            || this.reservation.hasSelectedBothCustomBaggage;
    }

    get hasSelectedPaidReturnBaggage(): boolean {
        return this.reservation.hasSelectedReturnCustomBaggage
            || this.reservation.hasSelectedBothCustomBaggage;
    }

    mounted() {
        this.mountedTimeStampMs = new Date().getTime();
        // Redirect to mobile
        if (G2S.config.mobileRedirectAllowed) {
            window.location.href = G2S.config.mobileUrl +
                this.$route.path +
                (window.location.search && window.location.search.length > 1 ? window.location.search : '');
            return;
        }

        window.document.title = i18next.t('title_flight_tickets_booking');

        if (!this.followWasMadeFromSearchPage) {
            window.history.replaceState({ ...window.history.state }, '', new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
            window.history.pushState({ ...window.history.state, view: 'passengers' }, '', router.currentRoute.fullPath);
        } else {
            window.history.replaceState({ ...window.history.state, view: 'passengers' }, '', router.currentRoute.fullPath);
        }
        this.setLastHistoryStateView();
        window.addEventListener('popstate', this.onPopState);

        this.loadModel();
    }

    beforeDestroy() {
        window.removeEventListener('popstate', this.onPopState);
    }

    setLastHistoryStateView() {
        this.lastHistoryStateView = window.history.state && window.history.state.view ? window.history.state.view : '';
    }

    onPopState(event) {
        if (event.state && event.state.view == 'details' && this.lastHistoryStateView != 'details' && this.lastHistoryStateView != 'after-payment') {
            this.showFlightDetails();
        }
        if (event.state && event.state.view == 'passengers' && this.lastHistoryStateView != 'passengers' && this.lastHistoryStateView != 'after-payment') {
            this.showBookingForm();
            if (this.canGoBackOrForwardToPassengers) {
                this.moveOnStep('passengers');
            }
        }
        if (event.state && event.state.view == 'seats' && this.lastHistoryStateView != 'seats' && this.lastHistoryStateView != 'after-payment') {
            this.showBookingForm();
            if (this.canGoBackOrForwardToPassengers) {
                this.moveOnStep('seats');
            }
        }
        if (event.state && event.state.view == 'services' && this.lastHistoryStateView != 'services' && this.lastHistoryStateView != 'after-payment') {
            this.showBookingForm();
            if (this.canGoBackOrForwardToPassengers) {
                this.moveOnStep('services');
            }
        }
        if (event.state && event.state.view == 'payment' && this.lastHistoryStateView != 'payment' && this.lastHistoryStateView != 'after-payment') {
            this.showBookingForm();
            if (this.canGoBackOrForwardToPassengers) {
                this.moveOnStep('payment');
            }
        }
        if (event.state && event.state.view && this.lastHistoryStateView == 'after-payment') {
            window.history.pushState({ ...window.history.state, view: 'after-payment' }, '', router.currentRoute.fullPath);
            this.setLastHistoryStateView();
        }
    }

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }
    @Watch('formStep')
    onformStepChange(formStep: FormStep) {
        switch (formStep) {
            case 'passengers':
                (<any>this).$ga.sendPageview('/flights/booking/start', 'Flights / Booking / Start', this.gaParams);
                break;
            case 'services':
                (<any>this).$ga.sendPageview('/flights/booking/step2', 'Flights / Booking / Step2', this.gaParams);
                break;
            case 'payment':
                (<any>this).$ga.sendPageview('/flights/booking/step3', 'Flights / Booking / Step3', this.gaParams);
                break;
        }
    }

    @Watch('reservation.selectedFlightId')
    onReservationSelectedFlightIdChanged() {
        // Flight changed - custom baggage should be updated
        this.reservation.resetAllSelectedBaggage();
        this.reservation.resetAllSelectedSeats();

        if (this.reservation.flights.length > 1
            && this.reservation.selectedFlightId !== this.flightBookingDetails!.selectedFlightUniqueId) {
            const brandNum = this.gaAlreadySubmitted.includes('flights.booking.btn.brands') ? '2' : '';
            if (!this.gaAlreadySubmitted.includes('flights.booking.brands.changed' + brandNum)) {
                (<any>this).$ga.sendEvent('flights.booking', 'brands.changed' + brandNum, 'flights.booking.brands.changed' + brandNum, this.gaParams);
                this.gaAlreadySubmitted.push('flights.booking.brands.changed' + brandNum);
            }
        }

        this.selectedFlightAdapter = this.reservation.selectedFlight!.adapter;
        this.selectedFlightAirlinesCode = this.reservation.selectedFlight!.airlinesCode;
        this.selectedFlightTfid = this.reservation.selectedFlight!.tfid;

        if (!this.baggageQueryParamAlreadyHandled) {
            this.handleBaggageQueryParam();
        }
    }

    isMarkerOwner(markerOwner: string): boolean {
        return utils.Cookies.getMarkerOwner() === markerOwner;
    }

    loadModel() {
        // Handle old-school links
        if (!this.searchFrom
            && !this.searchTo
            && this.$route.query
            && this.$route.query.search) {
            router.push(new flights.FlightNavigationService().getBookingUrlNotParsed(this.$route.query.search as string, this.searchId, this.flightId));
            return;
        }

        if (!this.searchParams) {
            router.push('/flights');
            return;
        }

        if (!this.searchId || !this.flightId) {
            router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams));
            return;
        }

        document.getElementById('pixels')!.innerHTML = '';

        // Init GA params
        (<any>this).$ga.addSearchParams(this.gaParams, this.searchParams);

        // Update tracking params
        utils.TrackingService.updateParamsFromSearch(this.trackParams, this.searchParams);

        const momentInit = moment();

        (<any>this).$ga.sendPageview('/flights/booking/init', 'Flights / Booking / Init', this.gaParams);

        utils.TrackingService.track(utils.TrackEvent.BookingInit, this.trackParams);

        this.view = 'loading';
        this.formStep = 'passengers';

        const request: api.GetFlightBookingDetailsRequest = {
            baggage: this.$route.query.baggage as string || '',
            searchId: this.searchId,
            flightId: this.flightId,
            gaClientId: utils.Cookies.getGaClientId(),
            clientDevice: G2S.config.clientDevice,
            skipCheckBooking: api.ReservationService.skipCheckBookingOnFlightBookingDetails,
            partnerCookie: utils.Cookies.getPartnerCookie(),
            markerReferrer: utils.Cookies.getMarkerReferrer(),
            queryString: window.location.search.toString()
        };
        const reservationService = new api.ReservationService();

        reservationService.getFlightBookingDetails(request).then(
            (result: api.FlightBookingDetails) => {
                this.gaParams.metric9 = Math.round(moment.duration(moment().diff(momentInit)).as('seconds'));
                this.trackParams.InitStartDuration = this.gaParams.metric9;

                if (result.bookingPossible
                    && result.paymentTypes
                    && result.paymentTypes.length > 0) {
                    this.flightBookingDetails = result;
                    
                    this.reservation = new flights.ReservationModel(this.flightBookingDetails);
                    this.handleBaggageQueryParam();
                    this.view = 'form';
                    
                    const checkFlightConfirmRequest: api.CheckFlightConfirmRequest = {
                        bookingSessionId: this.flightBookingDetails.bookingSessionId,
                        elapsedMsClient: new Date().getTime() - this.mountedTimeStampMs,
                        flightId: this.flightBookingDetails.flights[0].uniqueId,
                        searchId: this.searchId
                    };

                    reservationService
                        .checkFlightConfirm(checkFlightConfirmRequest)
                        .catch((error) => {
                            (<any>this).$ga.reachGoal('is_client_success_backend');
                            if (error.response) {
                                console.log('api/checkFlight XHR failed', error.response.status, error.response.data);
                            } else {
                                console.log('api/checkFlight XHR error', error.message);
                            }
                        });

                    // Update GA params
                    this.gaParams.dimension2 = this.reservation.selectedFlight ? this.reservation.selectedFlight!.adapter + '_' : 'none'; // Add underscore for regex filtering in GA
                    this.gaParams.dimension3 = this.reservation.selectedFlight ? this.reservation.selectedFlight!.allFlightAirlinesCode.join('_') : 'none';
                    this.gaParams.dimension10 = this.reservation.selectedFlight!.multiFRType.toString();
                    this.gaParams.metric3 = this.searchParams!.passengers.adults + this.searchParams!.passengers.children + this.searchParams!.passengers.infants;

                    (<any>this).$ga.addFlightProduct(this.reservation.selectedFlight, this.reservation.passengers.length);
                    (<any>this).$ga.setAction('checkout', { 'step': 1 });

                    (<any>this).$ga.sendPageview('/flights/booking/start', 'Flights / Booking / Start', this.gaParams);

                    // Tracking
                    utils.TrackingService.updateParamsFromReservation(this.trackParams, this.reservation);
                    utils.TrackingService.track(utils.TrackEvent.BookingStart, this.trackParams);
                    try {
                        new api.RetailRocket().orderFormOpened({
                            flightId: result.flights[0].uniqueId,
                            searchId: this.searchId,
                            products: result.products,
                            mailTrackingId: <any>this.$route.query.MailTrackingId
                        });
                    }
                    catch (err) {
                        console.error(err);
                    }

                    if (this.reservation.selectedFlight
                        && this.reservation.selectedFlight.customBaggage
                        && this.reservation.selectedFlight.customBaggage.bookable) {
                        (<any>this).$ga.sendEvent('flights.booking', 'baggage.available', 'flights.booking.baggage.available', this.gaParams);
                    }

                    if (this.$auth.authenticated) {
                        this.$auth.getCurrentUserWithBonuses(this.flightBookingDetails.bookingSessionId).then(
                            (result: api.UserWithBonuses) => {
                                this.currentUser = result;

                                if (!this.reservation.contacts.email) {
                                    this.reservation.contacts.email = this.currentUser.email!;
                                }
                                if (!this.reservation.contacts.phone) {
                                    this.reservation.contacts.phone = this.$options.filters!.phone(this.currentUser.phone) || '';
                                }
                            }
                        );
                    }

                    // Check booking
                    this.reservation.flights.forEach((f: flights.FlightModel) => {
                        if (!f.bookingChecked && !f.uniqueId.includes('_')) {
                            const checkFlightRequest: api.CheckFlightRequest = {
                                searchId: this.searchId,
                                flightId: f.uniqueId,
                                bookingSessionId: this.flightBookingDetails!.bookingSessionId
                            };

                            reservationService.checkFlight(checkFlightRequest).then(
                                (checkResult: api.CheckFlightResponse) => {
                                    if (checkResult.flight.bookingPossible) {
                                        f.update(checkResult.flight);

                                        if (f.uniqueId === this.reservation.selectedFlightId) {
                                            this.reservation.resetAllSelectedBaggage();
                                            this.handleBaggageQueryParam();
                                        }
                                    } else {
                                        // Show out of stock dialog only if it's selected flight or it's last flight
                                        if (f.uniqueId === this.reservation.selectedFlightId
                                            || this.reservation.flights.length === 1) {
                                            this.showOutOfStockDialog();
                                        }

                                        this.reservation.deleteOutOfStockFlight(f.uniqueId);
                                    }
                                },
                                (error) => {
                                    // Show out of stock dialog only if it's selected flight or it's last flight
                                    if (f.uniqueId === this.reservation.selectedFlightId
                                        || this.reservation.flights.length === 1) {
                                        this.showOutOfStockDialog();
                                    }

                                    this.reservation.deleteOutOfStockFlight(f.uniqueId);

                                    if (error.response) {
                                        console.log('api/checkFlight XHR failed', error.response.status, error.response.data);
                                    } else {
                                        console.log('api/checkFlight XHR error', error.message);
                                    }
                                }
                            );
                        }
                    });

                    if (this.reservation.hasBrands) {
                        (<any>this).$ga.sendEvent('flights.booking', 'brands.available', 'flights.booking.brands.available', this.gaParams);
                    }

                    if (this.reservation.surchargeInfo?.type && this.reservation.surchargeInfo.clientText && this.reservation.surchargeInfo.surchargeAmount) {
                        (<any>this).$ga.sendEvent('flights.booking', 'price.change', 'price.change.shown.popup', this.gaParams);
                        (<any>this.$root).$refs.dialogMessage.show({
                            text: this.reservation.surchargeInfo.clientText,
                            btnOkLabel: this.reservation.surchargeInfo.buttonAcceptText,
                            btnCancelLabel: this.reservation.surchargeInfo.buttonRejectText,
                            btnCancelColorClass: 'btn-default',
                            persistent: true,
                            btnOkHandler: () => {
                                (<any>this).$ga.sendEvent('flights.booking', 'price.change', 'price.change.btn.click.select', this.gaParams);
                            },
                            btnCancelHandler: () => {
                                (<any>this).$ga.sendEvent('flights.booking', 'price.change', 'price.change.btn.click.new.search', this.gaParams);
                                router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                            }
                        });
                    }

                    if (this.flightBookingDetails?.notifications.length) {
                        (<any>this).$ga.sendEvent('flights.booking', 'info.airport.closed', 'flights.booking.info.airport.closed', this.gaParams);
                    }

                    // Restore form data from cookie
                    utils.Cookies.restoreReservationModelCookie(this.reservation);

                    // Aviasales passenger input hadling
                    (<any>window).AviasalesInject.setPassengers(this.reservation.passengers, this.reservation.countries);

                    utils.PixelsService.setPixelForFlightBooking(document.getElementById('pixels')!, this.searchParams!, this.reservation.selectedFlight!.billingPrice);

                    if (this.$route.query['confirmationPopup']) {
                        const p = this.reservation.getProductByExternalId(this.$route.query['confirmationPopup'] as string);

                        if (p) {
                            this.productIdToConfirm = p.product.id;
                            this.modalProductConfirmTestMode = true;
                            (<any>this.$refs).modalProductConfirm.isActive = true;
                        }
                    }

                    // For confirmation testing!
                    //new api.ReservationService().getReservationStatus('3f0489b8-c867-4a76-bbc1-874a215a4971', false, false).then(
                    //    (result: api.GetReservationStatusResult) => {
                    //        this.reservationInfo = result.reservationInfo;

                    //        this.viewForm = 'confirmation';
                    //    },
                    //    (error) => {

                    //    }
                    //);
                } else {
                    (<any>this.$root).$refs.dialogMessage.show({
                        text: i18next.t('label_flight_out_of_stock'),
                        persistent: true,
                        btnOkHandler: () => {
                            router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                        }
                    });

                    if (result.paymentTypes
                        && result.paymentTypes.length > 0) {
                        (<any>this).$ga.sendEvent('flights.booking', 'msg.outofstock', 'flights.booking.msg.outofstock', this.gaParams);

                        utils.TrackingService.track(
                            utils.TrackEvent.BookingOutOfStock,
                            this.trackParams,
                            {
                                ErrorReason: 'OutOfStock'
                            });
                    } else {
                        (<any>this).$ga.sendEvent('flights.booking', 'msg.paymentnotavailable', 'flights.booking.msg.paymentnotavailable', this.gaParams);

                        utils.TrackingService.track(
                            utils.TrackEvent.BookingOutOfStock,
                            this.trackParams,
                            {
                                ErrorReason: 'NoPaymentOptions'
                            });
                    }
                }

                this.gaParams.metric9 = undefined;
                this.trackParams.InitStartDuration = undefined;

                // For confirmation testing! Do not commit uncommented!
                //new api.ReservationService().getReservationStatus('3f0489b8-c867-4a76-bbc1-874a215a4971', false, false).then(
                //    (result: api.GetReservationStatusResult) => {
                //        this.reservationInfo = result.reservationInfo;

                //        this.viewForm = 'confirmation';
                //    },
                //    (error) => {

                //    }
                //);
            },
            (error) => {
                this.gaParams.metric9 = Math.round(moment.duration(moment().diff(momentInit)).as('seconds'));

                let errorMessage = i18next.t('label_flight_out_of_stock');

                if (error.response) {
                    if (error.response.status === 404) {
                        errorMessage = i18next.t('label_flight_expired');
                    }
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                (<any>this.$root).$refs.dialogMessage.show({
                    text: errorMessage,
                    btnOkLabel: i18next.t('button_search'),
                    persistent: true,
                    btnOkHandler: () => {
                        router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                    }
                });

                if (errorMessage === i18next.t('label_flight_expired')) {
                    (<any>this).$ga.sendEvent('flights.booking', 'msg.expired.404', 'flights.booking.msg.expired.404', this.gaParams);

                    utils.TrackingService.track(
                        utils.TrackEvent.BookingOutOfStock,
                        this.trackParams,
                        {
                            ErrorReason: '404',
                            InitStartDuration: this.gaParams.metric9
                        });
                } else {
                    (<any>this).$ga.sendEvent('flights.booking', 'msg.expired.500', 'flights.booking.msg.expired.500', this.gaParams);

                    utils.TrackingService.track(
                        utils.TrackEvent.BookingOutOfStock,
                        this.trackParams,
                        {
                            ErrorReason: '500',
                            InitStartDuration: this.gaParams.metric9
                        });
                }

                this.gaParams.metric9 = undefined;
            }
        );
    }

    onBooking() {
        if (this.view !== 'booking') {
            this.view = 'booking';
        }

        if (G2S.config.theme === 'megotravel') {
            return;
        }

        const reservationService = new api.ReservationService();

        // Save user's input to cookie
        utils.Cookies.saveReservationModelCookie(this.reservation);

        const selectedPayment = this.reservation.payment!.selectedPaymentType;
        const selectedPaymentMethod = selectedPayment.isYandexPay ? this.reservation.payment!.selectedPaymentMethod : '';
        if (!selectedPayment.isYandexPay) {
            (<any>this).$ga.sendPageview(
                `/flights/booking/payment/${selectedPayment.kind}/${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '/' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                `Flights / Booking / Payment / ${selectedPayment.kind} / ${selectedPayment.paymentTypeId}${selectedPaymentMethod ? ' /' + selectedPaymentMethod.type : ''}`,
                this.gaParams
            );
        }

        if (!this.anyProductConfirmationWasShown) {
            (<any>this).$ga.sendEvent('flights.booking', 'product.confirm.empty', 'flights.booking.product.confirm.empty', this.gaParams);
        }
        (<any>this).$ga.sendEvent('flights.booking', 'product.confirm.finished', 'flights.booking.product.confirm.finished', this.gaParams);

        const request: api.CreateReservationRequest = reservationService.createReservationRequest(
            this.searchId,
            this.reservation,
            this.flightBookingDetails!,
            G2S.config.clientDevice
        );

        if (
            this.reservation.payment!.selectedPaymentType.kind === 'CreditCardInplace'
            && !request.creditCard
            && !this.reservation.payment!.selectedPaymentType.isYandexPay
            && this.reservation.payment!.selectedPaymentTypeId !== 'MobiMoneySbp'
        ) {
            (<any>this.$root).$refs.dialogMessage.show({
                text: i18next.t('error_payment_card_not_available')
            });

            this.view = 'form';

            (<any>this).$ga.sendEvent(
                'flights.booking',
                `payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.notavailable`.toLowerCase(),
                `flights.booking.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.notavailable`.toLowerCase(),
                this.gaParams
            );

            return;
        }

        //if (G2S.isDev) {
        //    console.log('DEBUG: BOOKING INTERRUPTED');
        //    return;
        //}

        this.bookingCompleted = false;
        window.history.pushState({ ...window.history.state, view: 'after-payment' }, '', router.currentRoute.fullPath);
        this.setLastHistoryStateView();

        // Aviasales passenger input handling: cleanup
        (<any>window).AviasalesInject.resetPassengers();

        const createOrRestartReservationHandleResult = (result: api.CreateReservationResult, canRecursion: boolean = false) => {
            if (result.reservationInfo) {
                new api.RetailRocket().reservationCreated({
                    flightKey: api.RetailRocketCL.flightKey,
                    reservationId: Number(result.reservationInfo.reservationNumber),
                    products: this.reservation.selectedProducts.map((product) => product.product)
                });
            }

            if (result.paymentInfo) {
                this.reservationInfo = result.reservationInfo;
                this.paymentInfo = result.paymentInfo;
                this.authToken = result.authToken;
                if (canRecursion) {
                    (<any>this).$ga.sendPageview('/flights/booking/success', 'Flights / Booking / Success', this.gaParams);
                }

                switch (result.paymentInfo.errorCode) {
                    case 6200: {
                        if (this.reservation.selectedProducts.length > 0 && !this.hasFlightService(this.reservation.passengers)) {
                            new api.ReservationService().updateBookingSessionNotEnoughMoneyPopup({
                                bookingSessionId: this.flightBookingDetails!.bookingSessionId
                            }).catch(
                                () => console.error('Error when sending the request')
                            );
                        }
                        break;
                    }
                }

                utils.TrackingService.track(
                    utils.TrackEvent.BookingSuccess,
                    this.trackParams
                );

                const showPaymentSplitDialogOrDoNext = (next: () => any) => {
                    if (result.paymentInfo.split && result.paymentInfo.split.airlineAmount > 0) {
                        const split = result.paymentInfo.split;
                        const message = `${i18next.t(
                            'label_payment_info_split_message',
                            <any>{
                                i: `<b>${Filters.priceWithCurrency(
                                    split.agencyAmount + split.airlineAmount,
                                    split.agencyAmountCurrency,
                                    false
                                )}</b>`,
                                interpolation: { escapeValue: false }
                            })}:
                            <ul>
                                <li>
                                    <b>${Filters.priceWithCurrency(split.agencyAmount, split.agencyAmountCurrency, false)}</b>
                                    ${i18next.t('label_payment_info_split_to_agency')}
                                </li>
                                <li>
                                    <b>${Filters.priceWithCurrency(split.airlineAmount, split.airlineAmountCurrency, false)}</b>
                                    ${i18next.t('label_payment_info_split_to_airline')}
                                </li>
                            </ul>`;

                        if (G2S.config.theme === 'aviasales') {
                            (<any>this.$root.$refs).fullScreenDialog.show({
                                title: i18next.t('label_payment_info_split_title'),
                                text: message,
                                firstBtnText: i18next.t('button_next'),
                                firstBtnFn: () => {
                                    (<any>this.$root.$refs).fullScreenDialog.hide();
                                    next();
                                }
                            });
                        } else {
                            (<any>this.$root).$refs.dialogMessage.show({
                                title: i18next.t('label_payment_info_split_title'),
                                text: message,
                                persistent: true,
                                btnOkLabel: i18next.t('button_next'),
                                btnOkHandler: () => {
                                    next();
                                }
                            });
                        }
                    } else {
                        next();
                    }
                };

                if (result.paymentInfo.inPlace) {
                    
                    const handleFinishResponse = (result: api.CreateReservationResult) => {
                        if (result.paymentInfo.inPlace.succeed && result.paymentInfo?.inPlace?.status !== 'Error') {
                            if (result.paymentInfo.redirect) {
                                // 3DS redirect
                                showPaymentSplitDialogOrDoNext(() => {
                                    utils.Payment.redirect(result.paymentInfo.redirect);
                                });

                                utils.TrackingService.track(
                                    utils.TrackEvent.BookingSuccessRedirect,
                                    this.trackParams
                                );
                            } else {
                                this.bookingCompleted = true;

                                // Wait 10 seconds to give time to server to issue tickets and change status.
                                setTimeout(() => {
                                    showPaymentSplitDialogOrDoNext(() => {
                                        if (result.reservationInfo.reservationPublicId) {
                                            const authToken = result.authToken ? '?auth_code=' + result.authToken : '';
                                            router.push('/order/' + result.reservationInfo.reservationPublicId + authToken);
                                        } else {
                                            this.moveOnStep('confirmation');
                                            this.view = 'form';
                                        }
                                    });
                                }, 10000);

                                utils.TrackingService.track(utils.TrackEvent.BookingSuccessPaid, this.trackParams);
                            }
                        } else {
                            (<any>this).$ga.sendEvent(
                                'flights.booking',
                                `error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                `flights.booking.error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                this.gaParams
                            );

                            // Order created but not payed > redirect to restart payment
                            if (result.paymentInfo.inPlace.exceptionMessage) {
                                console.error('Payment error: ' + result.paymentInfo.inPlace.exceptionMessage);
                            }
                            
                            this.$refs.dialogSbp.close();

                            if (G2S.config.theme === 'aviasales') {
                                (<any>this.$root.$refs).fullScreenDialog.show({
                                    title: i18next.t('error'),
                                    text: result.paymentInfo.inPlace.errorMessage,
                                    firstBtnText: i18next.t('button_next'),
                                    firstBtnFn: () => {
                                        location.href = '/order/payment/' + result.reservationInfo.reservationPublicId;
                                    }
                                });
                            } else {
                                (<any>this.$root).$refs.dialogMessage.show({
                                    text: result.paymentInfo.inPlace.errorMessage,
                                    persistent: true,
                                    btnOkHandler: () => {
                                        location.href = '/order/payment/' + result.reservationInfo.reservationPublicId;
                                    }
                                });
                            }

                            utils.TrackingService.track(utils.TrackEvent.BookingError, this.trackParams, { ErrorReason: 'Payment' });
                        }
                    };
                    
                    if (result.paymentInfo.inPlace.status === 'SbpQr' && result.paymentInfo.redirect) {
                        const showReturnLink = this.reservation.payment.paymentTypes.length > 1;
                        this.$refs.dialogSbp.show(result.paymentInfo.redirect.url, showReturnLink);
                        
                        const getPaymentQrStatusRequest: api.FinishPreauth3dsRequest = {
                            reservationPublicId: result.reservationInfo.reservationPublicId,
                            billPublicId: result.paymentInfo.billPublicId,
                            paymentId: result.paymentInfo.paymentId
                        };
                        
                        // polling the backend until payment status changes
                        reservationService.pollPaymentQrStatus(getPaymentQrStatusRequest)
                            .then((pollResult) => {
                                this.$refs.dialogSbp.close();
                                handleFinishResponse(<api.CreateReservationResult>pollResult);
                            })
                            .catch((error) => {
                                this.$refs.dialogSbp.close();
                                this.view = 'form';
                                this.$nextTick(() => {
                                    (<any>this.$refs).flightsBookingPayment.$el.scrollIntoView({ behavior: 'smooth' });
                                });
                                if (error) {
                                    if (G2S.config.theme === 'aviasales') {
                                        (<any>this.$root.$refs).fullScreenDialog.show({
                                            title: i18next.t('error'),
                                            text: error.message,
                                            firstBtnText: 'Попробовать ещё раз',
                                            firstBtnFn: () => {
                                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                            }
                                        });
                                    } else {
                                        (<any>this.$root).$refs.dialogMessage.show({
                                            text: error.message
                                        });
                                    }
                                }
                            });
                        
                    } else if (result.paymentInfo.inPlace.succeed) {
                        if (result.paymentInfo.inPlace.status === 'Preauth3ds'
                            && result.paymentInfo.redirect) {
                            console.log('Preauth3ds');

                            utils.Http.hiddenFrameWithPost(result.paymentInfo.redirect);

                            // Wait for DOM issues.
                            setTimeout(() => {
                                const finishRequest: api.FinishPreauth3dsRequest = {
                                    reservationPublicId: result.reservationInfo.reservationPublicId,
                                    billPublicId: result.paymentInfo.billPublicId,
                                    paymentId: result.paymentInfo.paymentId
                                };

                                reservationService.finishPreauth3ds(finishRequest).then(
                                    (result: api.CreateReservationResult) => {
                                        handleFinishResponse(result);
                                    },
                                    (error) => {
                                        (<any>this).$ga.sendEvent(
                                            'flights.booking',
                                            `error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                            `flights.booking.error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                            this.gaParams
                                        );

                                        if (error.response) {
                                            console.log('api/finishPreauth3ds XHR failed', error.response.status, error.response.data);
                                        } else {
                                            console.log('api/finishPreauth3ds XHR error', error.message);
                                        }

                                        if (result.paymentInfo.errorCode === 6200 && this.reservation.selectedProducts && this.reservation.selectedProducts.length > 0 && !this.hasFlightService(this.reservation.passengers)) {
                                            (<any>this).$ga.sendEvent(
                                                'payment',
                                                'error',
                                                'payment.error.limit.card',
                                                this.gaParams
                                            );
            
                                            (<any>this.$refs).modalError6200.isActive = true;
                                        } else {
                                            if (G2S.config.theme === 'aviasales') {
                                                (<any>this.$root.$refs).fullScreenDialog.show({
                                                    title: i18next.t('error'),
                                                    text: i18next.t('error_payment_failed'),
                                                    firstBtnText: 'Попробовать ещё раз',
                                                    firstBtnFn: () => {
                                                        location.href = '/order/payment/' + result.reservationInfo.reservationPublicId;
                                                    }
                                                });
                                            } else {
                                                (<any>this.$root).$refs.dialogMessage.show({
                                                    text: i18next.t('error_payment_failed'),
                                                    persistent: true,
                                                    btnOkHandler: () => {
                                                        location.href = '/order/payment/' + result.reservationInfo.reservationPublicId;
                                                    }
                                                });
                                            }
                                        }

                                        utils.TrackingService.track(utils.TrackEvent.BookingError, this.trackParams, { ErrorReason: 'Payment' });
                                    }
                                );
                            }, 100);
                        } else if (result.paymentInfo.redirect) {
                            // 3DS redirect
                            showPaymentSplitDialogOrDoNext(() => {
                                utils.Payment.redirect(result.paymentInfo.redirect);
                            });

                            utils.TrackingService.track(
                                utils.TrackEvent.BookingSuccessRedirect,
                                this.trackParams
                            );
                        } else {
                            this.bookingCompleted = true;

                            // Wait 10 seconds to give time to server to issue tickets and change status.
                            setTimeout(() => {
                                showPaymentSplitDialogOrDoNext(() => {
                                    if (result.reservationInfo.reservationPublicId) {
                                        router.push('/order/' + result.reservationInfo.reservationPublicId);
                                    } else {
                                        this.moveOnStep('confirmation');
                                        this.view = 'form';
                                    }
                                });
                            }, 10000);

                            utils.TrackingService.track(
                                utils.TrackEvent.BookingSuccessPaid,
                                this.trackParams
                            );
                        }
                    } else {
                        if (result.reservationInfo) {
                            (<any>this).$ga.sendEvent(
                                'flights.booking',
                                `error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                `flights.booking.error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                this.gaParams
                            );
                            // Order created but not payed > redirect to restart payment
                            if (result.paymentInfo.inPlace.exceptionMessage) {
                                console.error('Payment error: ' + result.paymentInfo.inPlace.exceptionMessage);
                            }

                            if (result.paymentInfo.errorCode === 6200 && this.reservation.selectedProducts && this.reservation.selectedProducts.length > 0 && !this.hasFlightService(this.reservation.passengers)) {
                                (<any>this).$ga.sendEvent(
                                    'payment',
                                    'error',
                                    'payment.error.limit.card',
                                    this.gaParams
                                );

                                (<any>this.$refs).modalError6200.isActive = true;
                            } else {
                                if (G2S.config.theme === 'aviasales') {
                                    (<any>this.$root.$refs).fullScreenDialog.show({
                                        title: i18next.t('error'),
                                        text: result.paymentInfo.inPlace.errorMessage,
                                        firstBtnText: 'Попробовать ещё раз',
                                        firstBtnFn: () => {
                                            location.href = '/order/payment/' + result.reservationInfo.reservationPublicId;
                                        }
                                    });
                                } else {
                                    (<any>this.$root).$refs.dialogMessage.show({
                                        text: result.paymentInfo.inPlace.errorMessage,
                                        persistent: true,
                                        btnOkHandler: () => {
                                            location.href = '/order/payment/' + result.reservationInfo.reservationPublicId;
                                        }
                                    });
                                }
                            }

                            utils.TrackingService.track(
                                utils.TrackEvent.BookingError,
                                this.trackParams,
                                {
                                    ErrorReason: 'Payment'
                                }
                            );
                        } else {
                            // Reservation not created - input data error
                            if (result.paymentInfo.inPlace.exceptionMessage) {
                                console.error('Booking error: ' + result.paymentInfo.inPlace.exceptionMessage);
                            }

                            this.view = 'form';

                            if (G2S.config.theme === 'aviasales') {
                                (<any>this.$root.$refs).fullScreenDialog.show({
                                    title: i18next.t('error'),
                                    text: result.paymentInfo.inPlace.errorMessage,
                                    firstBtnText: 'Попробовать ещё раз',
                                    firstBtnFn: () => {
                                        (<any>this.$root.$refs).fullScreenDialog.hide();
                                        router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                                    }
                                });
                            } else {
                                (<any>this.$root).$refs.dialogMessage.show({
                                    text: result.paymentInfo.inPlace.errorMessage,
                                    persistent: true,
                                    btnOkHandler: () => {
                                        router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                                    }
                                });
                            }

                            utils.TrackingService.track(
                                utils.TrackEvent.BookingError,
                                this.trackParams,
                                {
                                    ErrorReason: 'Booking'
                                }
                            );
                        }
                    }
                } else if (result.paymentInfo.redirect) {
                    // Payment requires redirecting to external url
                    showPaymentSplitDialogOrDoNext(() => {
                        utils.Payment.redirect(result.paymentInfo.redirect);
                    });

                    utils.TrackingService.track(
                        utils.TrackEvent.BookingSuccessRedirect,
                        this.trackParams
                    );
                } else if (result.reservationInfo.surchargeInfo && canRecursion) {
                    const request: api.StartedReservation = {
                        reservationPublicId: result.reservationInfo.reservationPublicId
                    };
                    (<any>this).$ga.sendEvent(
                        'flights.booking',
                        'msg.price.rise',
                        'flights.booking.msg.price.rise',
                        this.gaParams
                    );
                    
                    if (G2S.config.theme === 'aviasales') {
                        (<any>this.$root.$refs).fullScreenDialog.show({
                            text: result.reservationInfo.surchargeInfo.clientText,
                            firstBtnText: result.reservationInfo.surchargeInfo.buttonAcceptText,
                            firstBtnFn: () => {
                                (<any>this.$root.$refs).fullScreenDialog.hide();
                                (<any>this).$ga.sendEvent(
                                    'flights.booking',
                                    'msg.price.rise',
                                    'flights.booking.msg.price.rise.ok',
                                    this.gaParams
                                );
                                reservationService.CompleteStartedReservation(request)
                                    .then((result: api.CreateReservationResult)=>{
                                        createOrRestartReservationHandleResult(result);
                                    },
                                    (error) => {
                                        console.log(error);
                                        createOrRestartReservationHandleResult(result);
                                    });
                            },
                            secondBtnText: result.reservationInfo.surchargeInfo.buttonRejectText,
                            secondBtnFn: () => {
                                (<any>this).$ga.sendEvent(
                                    'flights.booking',
                                    'msg.price.rise',
                                    'flights.booking.msg.price.rise.search',
                                    this.gaParams
                                );
                                reservationService.RejectStartedReservation(request)
                                    .then((result) => {
                                        console.log(result);
                                        router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                                    },
                                    (error) => {
                                        console.log(error);
                                    });
                            }
                        });
                    } else {
                        (<any>this.$root).$refs.dialogMessage.show({
                            text: result.reservationInfo.surchargeInfo.clientText,
                            btnOkLabel: result.reservationInfo.surchargeInfo.buttonAcceptText,
                            btnCancelLabel: result.reservationInfo.surchargeInfo.buttonRejectText,
                            btnCancelColorClass: 'btn-default',
                            persistent: true,
                            btnOkHandler: () => {
                                (<any>this).$ga.sendEvent(
                                    'flights.booking',
                                    'msg.price.rise',
                                    'flights.booking.msg.price.rise.ok',
                                    this.gaParams
                                );
                                reservationService.CompleteStartedReservation(request)
                                    .then((result: api.CreateReservationResult)=>{
                                        createOrRestartReservationHandleResult(result);
                                    },
                                    (error) => {
                                        console.log(error);
                                        createOrRestartReservationHandleResult(result);
                                    });
                            },
                            btnCancelHandler: () => {
                                (<any>this).$ga.sendEvent(
                                    'flights.booking',
                                    'msg.price.rise',
                                    'flights.booking.msg.price.rise.search',
                                    this.gaParams
                                );
                                reservationService.RejectStartedReservation(request)
                                    .then((result) => {
                                        console.log(result);
                                        router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
                                    },
                                    (error) => {
                                        console.log(error);
                                    });
                            }
                        });
                    }                
                } else {
                    console.log('PayLater');
                    
                    // PayLater
                    if (result.reservationInfo.reservationPublicId) {
                        router.push('/order/' + result.reservationInfo.reservationPublicId);
                    } else {
                        this.moveOnStep('confirmation');
                        this.view = 'form';
                    }
                }
            } else {
                // We have no paymentInfo (existed duplicate reservation was found) so just redirect to order page.
                (<any>this).$ga.sendEvent('flights.booking', 'booking.exists', 'flights.booking.exists', this.gaParams);
                router.push('/order/' + result.reservationInfo.reservationPublicId);
            }
        };
        
        // prevent request duplication for SBP
        if (selectedPayment.paymentTypeId === 'MobiMoneySbp'
            && this.paymentInfo?.inPlace?.status === 'SbpQr'
            && this.paymentInfo?.redirect) {
            createOrRestartReservationHandleResult(<api.CreateReservationResult>{
                paymentInfo: this.paymentInfo,
                reservationInfo: this.reservationInfo,
                authToken: this.authToken
            });
            
            return;
        }

        reservationService.createOrRestartReservation(request).then(
            (result: api.CreateReservationResult) => {              
                createOrRestartReservationHandleResult(result, true);
            },
            (error) => {
                if (error.response) {
                    console.log('api/createOrRestartReservation XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('api/createOrRestartReservation XHR error', error.message);
                }

                if (selectedPayment && error?.response && error?.response?.status) {
                    (<any>this).$ga.sendEvent(
                        'flights.booking',
                        `error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.${error?.response?.status}`.toLowerCase(),
                        `flights.booking.error.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.${error?.response?.status}`.toLowerCase(),
                        this.gaParams
                    );
                }

                if (error?.response?.status === 400) {
                    // Bad request: reservation was not created. Show message on booking form.
                    this.view = 'form';

                    if (G2S.config.theme === 'aviasales') {
                        (<any>this.$root.$refs).fullScreenDialog.show({
                            title: i18next.t('error'),
                            text: error.response.data.error.message,
                            firstBtnText: 'Попробовать ещё раз',
                            firstBtnFn: () => {
                                (<any>this.$root.$refs).fullScreenDialog.hide();
                            }
                        });
                    } else {
                        (<any>this.$root).$refs.dialogMessage.show({
                            text: error.response.data.error.message
                        });
                    }

                    utils.TrackingService.track(
                        utils.TrackEvent.BookingError,
                        this.trackParams,
                        {
                            ErrorReason: 'BadRequest'
                        }
                    );

                    if (error?.response?.data) {
                        throw 'api/createOrRestartReservation XHR failed' + error?.response?.data?.message;
                    }

                    return;
                }

                if (!error.response && error.message == 'Network Error') {
                    // Network error.
                    this.view = 'network-error';

                    utils.TrackingService.track(
                        utils.TrackEvent.BookingError,
                        this.trackParams,
                        {
                            ErrorReason: 'Network'
                        }
                    );

                    throw 'api/createOrRestartReservation XHR error' + error.message;

                    return;
                }

                // Server or another error.
                this.view = 'error';
                (<any>this).$ga.sendPageview('/flights/booking/error', 'Flights / Booking / Error', this.gaParams);

                utils.TrackingService.track(
                    utils.TrackEvent.BookingError,
                    this.trackParams,
                    {
                        ErrorReason: 'ServerSide'
                    }
                );

                if (error.message) {
                    throw 'api/createOrRestartReservation XHR error' + error.message;
                } else if (error?.response && error?.response.data) {
                    throw 'api/createOrRestartReservation XHR failed' + error?.response?.data?.message;
                }
            }
        );
    }

    handleBaggageQueryParam() {
        if (this.$route.query && this.$route.query.baggage) {
            this.reservation.selectCustomBaggageByQueryParam(this.$route.query.baggage as string);
        }
    }

    parseButtonLabel(v: string): string {
        if (v) {
            v = v.replace('[[happy]]', '<svg class="icon icon-smile-happy emoji"><use xlink:href="' + G2S.config.staticPathsWithHash['/i/icons/sprite.sm.svg'] + '#smile-happy"></use></svg>');
            v = v.replace('[[unhappy]]', '<svg class="icon icon-smile-unhappy emoji"><use xlink:href="' + G2S.config.staticPathsWithHash['/i/icons/sprite.sm.svg'] + '#smile-unhappy"></use></svg>');
        }

        return v;
    }

    replaceKeys(text: string, currentPrice: number | string, futurePrice: number | string, productPrice: number, currencyCode: string | undefined) {
        if (text) {
            return text
                .replace(/%order\.price\.current%/g, String((<any>this.$options.filters).price(currentPrice, false, currencyCode || '')))
                .replace(/%order\.price\.future%/g, String((<any>this.$options.filters).price(futurePrice, false, currencyCode || '')))
                .replace(/%product\.price%/g, String((<any>this.$options.filters).price(productPrice, false, currencyCode || '')));
        }
        return text;
    }

    showMeAccum = 0;
    brandContent: any = null;
    serveBrandConfirmation(confirmationData: flights.ProductServiceBrand) {
        this.productIdToConfirm = null;

        let p;
        if(confirmationData.services){
            p = utils.clone(confirmationData.services[this.showMeAccum]);
        }
        this.showMeAccum++;
        this.brandContent = p;
        if(this.brandContent && this.brandContent.products) {
            this.brandContent.products.sort((a,b) =>{
                return a.sortOrder == b.sortOrder ? 0 : (a.sortOrder > b.sortOrder ? 1 : -1);
            });
        }
        if(this.brandContent && this.brandContent.products){
            (<any>this.$refs).modalBrandsPopup.isActive = true;
        } else {
            this.closePopup();
        }
        this.anyProductConfirmationWasShown = true;

        new api.ReservationService().updateBookingSessionProductPopup({
            bookingSessionId: this.flightBookingDetails!.bookingSessionId,
            productPopupId: confirmationData.popup.popupId,
            popupType: confirmationData.popup.popupTypeNumber,
            isPopupOnForm: confirmationData.popup.position !== api.ProductPopupPosition.afterOrder
        }).catch(() => (<any>this).$ga.sendEvent('error', 'UpdateBookingSessionProductPopup', 'error.UpdateBookingSessionProductPopup'));
    }

    serveProductConfirmation(confirmationData: flights.ProductConfirmationData) {
        const currentPrice = this.reservation.getTotalPrice(true);
        const futurePrice = currentPrice + (confirmationData.product?.product.billingPrice ?? 0);

        const p = utils.clone(confirmationData.popup);
        p.buttonText1 = this.parseButtonLabel(this.replaceKeys(p.buttonText1, currentPrice, futurePrice, confirmationData.product?.product.billingPrice || 0, confirmationData.product?.product.currencyCode));
        p.buttonText2 = this.parseButtonLabel(this.replaceKeys(p.buttonText2, currentPrice, futurePrice, confirmationData.product?.product.billingPrice || 0, confirmationData.product?.product.currencyCode));
        p.buttonText3 = this.parseButtonLabel(this.replaceKeys(p.buttonText3, currentPrice, futurePrice, confirmationData.product?.product.billingPrice || 0, confirmationData.product?.product.currencyCode));
        p.buttonIcon1 = utils.String.camelToKebab(p.buttonIcon1);
        p.buttonIcon2 = utils.String.camelToKebab(p.buttonIcon2);
        p.buttonIcon3 = utils.String.camelToKebab(p.buttonIcon3);
        p.buttonColor1 = utils.String.camelToKebab(p.buttonColor1);
        p.buttonColor2 = utils.String.camelToKebab(p.buttonColor2);
        p.buttonColor3 = utils.String.camelToKebab(p.buttonColor3);
        p.buttonStyle1 = utils.String.camelToKebab(p.buttonStyle1);
        p.buttonStyle2 = utils.String.camelToKebab(p.buttonStyle2);
        p.buttonStyle3 = utils.String.camelToKebab(p.buttonStyle3);
        p.emTextColor = utils.String.camelToKebab(p.emTextColor);
        
        if (p.reservationPriceText) {
            const wrap = (content: any) => `<span style="text-decoration: underline; font-weight: bold">${content.toLocaleString('ru')}</span>`;

            const reservationPriceText = p.reservationPriceText
                .replace(/%order\.price\.current%/g, wrap(currentPrice))
                .replace(/%order\.price\.future%/g, wrap(futurePrice));

            if (reservationPriceText.match(/%[\w.]+%/)) {
                p.reservationPriceText = null;
            } else {
                p.reservationPriceText = reservationPriceText;
            }
        }

        this.modalConfirmProductContent = p;
        this.productIdToConfirm = confirmationData.product!.product.id;
        (<any>this.$refs).modalProductConfirm.isActive = true;
        this.anyProductConfirmationWasShown = true;

        (<any>this).$ga.sendEvent(
            'product.catchup',
            'shown',
            'product.catchup.kit' + this.modalConfirmProductContent?.kitId + '.id' + this.modalConfirmProductContent?.productId,
            this.gaParams
        );
        
        new api.ReservationService().updateBookingSessionProductPopup({
            bookingSessionId: this.flightBookingDetails!.bookingSessionId,
            productPopupId: confirmationData.popup.popupId,
            popupType: confirmationData.popup.popupTypeNumber,
            isPopupOnForm: confirmationData.popup.position !== api.ProductPopupPosition.afterOrder
        }).catch((error) => {
            (<any>this).$ga.sendEvent('error', 'UpdateBookingSessionProductPopup', 'error.UpdateBookingSessionProductPopup', this.gaParams);

            if (error?.response && error?.response?.status) {
                (<any>this).$ga.sendEvent('error', 'UpdateBookingSessionProductPopup', `error.UpdateBookingSessionProductPopup.${error.response.status}`, this.gaParams);
            }

            if (error.message) {
                throw 'api/updateBookingSessionProductPopup XHR error' + error.message;
            } else if (error?.response && error?.response.data) {
                throw 'api/updateBookingSessionProductPopup XHR failed' + error?.response?.data?.message;
            }
        });
    }
    serveProductDiscountConfirmation(confirmationData: flights.ProductDiscountConfirmationData) {
        const p = utils.clone(confirmationData.popup);
        p.buttonText1 = this.parseButtonLabel(p.buttonText1);
        p.buttonText2 = this.parseButtonLabel(p.buttonText2);
        p.buttonText3 = this.parseButtonLabel(p.buttonText3);
        p.buttonIcon1 = utils.String.camelToKebab(p.buttonIcon1);
        p.buttonIcon2 = utils.String.camelToKebab(p.buttonIcon2);
        p.buttonIcon3 = utils.String.camelToKebab(p.buttonIcon3);
        p.buttonColor1 = utils.String.camelToKebab(p.buttonColor1);
        p.buttonColor2 = utils.String.camelToKebab(p.buttonColor2);
        p.buttonColor3 = utils.String.camelToKebab(p.buttonColor3);
        p.buttonStyle1 = utils.String.camelToKebab(p.buttonStyle1);
        p.buttonStyle2 = utils.String.camelToKebab(p.buttonStyle2);
        p.buttonStyle3 = utils.String.camelToKebab(p.buttonStyle3);
        p.emTextColor = utils.String.camelToKebab(p.emTextColor);
        this.modalConfirmProductContent = p;
        console.log(this.modalConfirmProductContent);
        this.productIdToConfirm = confirmationData.productDiscount!.product.id;
        (<any>this.$refs).modalProductDiscountConfirm.isActive = true;
        this.anyProductConfirmationWasShown = true;
        
        (<any>this).$ga.sendEvent(
            'product.catchup.popup',
            'shown',
            'product.catchup.popup.kit' + this.modalConfirmProductContent?.kitId + '.id' + this.modalConfirmProductContent?.productId,
            this.gaParams
        );
        new api.ReservationService().updateBookingSessionProductPopup({
            bookingSessionId: this.flightBookingDetails!.bookingSessionId,
            productPopupId: confirmationData.popup.popupId,
            popupType: confirmationData.popup.popupTypeNumber,
            isPopupOnForm: confirmationData.popup.position !== api.ProductPopupPosition.afterOrder
        }).catch(() => (<any>this).$ga.sendEvent('error', 'UpdateBookingSessionProductPopup', 'error.UpdateBookingSessionProductPopup'));
    }

    serveBaggageUpgradeConfirmation(confirmationData: flights.BaggageConfirmationData) {
        if (confirmationData.baggageData) {
            this.modalUpgradeBaggageContent = {
                ...confirmationData.popup,
                isCloseIconVisible: confirmationData.popup.isCloseIconVisible,
                text: confirmationData.popup.text
                    .replace(
                        '%baggage.weight%',
                        confirmationData.baggageData.baggage
                    )
                    .replace(
                        '%baggage.price%',
                        confirmationData.baggageData.price
                    ),
                buttonText1: this.parseButtonLabel(confirmationData.popup.buttonText1),
                buttonText2: this.parseButtonLabel(confirmationData.popup.buttonText2),
                buttonText3: this.parseButtonLabel(confirmationData.popup.buttonText3),
                buttonIcon1: utils.String.camelToKebab(confirmationData.popup.buttonIcon1),
                buttonIcon2: utils.String.camelToKebab(confirmationData.popup.buttonIcon2),
                buttonIcon3: utils.String.camelToKebab(confirmationData.popup.buttonIcon3),
                buttonColor1: utils.String.camelToKebab(confirmationData.popup.buttonColor1),
                buttonColor2: utils.String.camelToKebab(confirmationData.popup.buttonColor2),
                buttonColor3: utils.String.camelToKebab(confirmationData.popup.buttonColor3),
                buttonStyle1: utils.String.camelToKebab(confirmationData.popup.buttonStyle1),
                buttonStyle2: utils.String.camelToKebab(confirmationData.popup.buttonStyle2),
                buttonStyle3: utils.String.camelToKebab(confirmationData.popup.buttonStyle3),
                emTextColor: utils.String.camelToKebab(confirmationData.popup.emTextColor)
            };
            this.anyProductConfirmationWasShown = true;
            if(confirmationData.baggageData) {
                (<any>this.$refs).modalBaggageUpgrade.isActive = true;
            } 
            (<any>this).$ga.sendEvent(
                'product.catchup',
                'shown',
                'product.catchup.kit' + this.modalUpgradeBaggageContent?.kitId + '.id' + this.modalUpgradeBaggageContent?.productId,
                this.gaParams
            );
            new api.ReservationService().updateBookingSessionProductPopup({
                bookingSessionId: this.flightBookingDetails!.bookingSessionId,
                productPopupId: confirmationData.popup.popupId,
                popupType: confirmationData.popup.popupTypeNumber,
                isPopupOnForm: confirmationData.popup.position !== api.ProductPopupPosition.afterOrder
            }).catch(() => (<any>this).$ga.sendEvent('error', 'UpdateBookingSessionProductPopup', 'error.UpdateBookingSessionProductPopup', this.gaParams));
        }
    }

    onBookingStateSubmit() {
        window.scrollTo(0, 0);

        const selectedPayment = this.reservation.payment!.selectedPaymentType;
        const selectedPaymentMethod = selectedPayment.isYandexPay ? this.reservation.payment!.selectedPaymentMethod : '';

        switch (this.formStep) {
            case 'passengers':
                if (!this.gaAlreadySubmitted.includes('flights.booking.step1.submit')) {
                    (<any>this).$ga.sendEvent('flights.booking', 'step1.submit', 'flights.booking.step1.submit', this.gaParams);
                    this.gaAlreadySubmitted.push('flights.booking.step1.submit');
                }

                if (this.reservation.selectedFlight!.bookingChecked && this.reservation.selectedFlight!.bookingPossible) {
                    const confirmationData = this.reservation.getConfirmationDataForUndonePopupByPosition(api.ProductPopupPosition.afterStep1);

                    if (confirmationData == 'skipPopupAndGo') {
                        this.onBookingStateSubmit();

                        return;
                    }
                    //test
                    if(confirmationData?.services) {
                        this.serveBrandConfirmation(confirmationData);

                        return;
                    }

                    if (confirmationData?.product) {
                        this.serveProductConfirmation(confirmationData);

                        return;
                    }

                    if (confirmationData?.baggageData) {
                        this.serveBaggageUpgradeConfirmation(confirmationData);

                        return;
                    }

                    this.canGoBackOrForwardToPassengers = true;

                    if (this.reservation?.trains.length) {
                        this.canGoBackOrForwardToSeats = true;
                        window.history.pushState({ ...window.history.state, view: 'seats' }, '', router.currentRoute.fullPath);
                        this.moveOnStep('seats');
                    } else {
                        this.canGoBackOrForwardToServices = true;
                        window.history.pushState({ ...window.history.state, view: 'services' }, '', router.currentRoute.fullPath);
                        this.moveOnStep('services');
                    }

                    
                    if (!this.gaAlreadySubmitted.includes('/flights/booking/step2')) {
                        (<any>this).$ga.addFlightProduct(this.reservation.selectedFlight, this.reservation.passengers.length);
                        (<any>this).$ga.setAction('checkout', { 'step': 2 });
                        this.gaAlreadySubmitted.push('/flights/booking/step2');

                        if (this.reservation?.trains.length) {
                            utils.TrackingService.track(
                                utils.TrackEvent.BookingStepSeats,
                                this.trackParams
                            );
                        } else {
                            utils.TrackingService.track(
                                utils.TrackEvent.BookingStepServices,
                                this.trackParams
                            );
                        }
                    }

                    // Save user's input to cookie
                    utils.Cookies.saveReservationModelCookie(this.reservation);

                    const request: api.UpdateBookingSessionRequest = {
                        bookingSessionId: this.flightBookingDetails!.bookingSessionId,
                        email: this.reservation.contacts.email,
                        phone: this.reservation.contacts.phone
                    };
                    const reservationService = new api.ReservationService();

                    reservationService.updateBookingSession(request);
                } else {
                    (<any>this.$root).$refs.dialogMessage.show({
                        title: i18next.t('title_flight_out_of_stock'),
                        text: i18next.t('label_flight_out_of_stock_fare')
                    });
                }
                break;
            case 'seats':
                this.canGoBackOrForwardToServices = true;
                window.history.pushState({ ...window.history.state, view: 'services' }, '', router.currentRoute.fullPath);

                this.moveOnStep('services');

                utils.TrackingService.track(
                    utils.TrackEvent.BookingStepServices,
                    this.trackParams
                );

                break;
            case 'services':
                if (!this.gaAlreadySubmitted.includes('flights.booking.step2.submit')) {
                    (<any>this).$ga.sendEvent('flights.booking', 'step2.submit', 'flights.booking.step2.submit', this.gaParams);
                    this.gaAlreadySubmitted.push('flights.booking.step2.submit');
                }

                const confirmationData = this.reservation.getConfirmationDataForUndonePopupByPosition(api.ProductPopupPosition.afterStep2);

                if (confirmationData == 'skipPopupAndGo') {
                    this.onBookingStateSubmit();

                    return;
                }
                //test
                if(confirmationData?.services) {
                    this.serveBrandConfirmation(confirmationData);

                    return;
                }

                if (confirmationData?.product) {
                    this.serveProductConfirmation(confirmationData);

                    return;
                }

                if (confirmationData?.baggageData) {
                    this.serveBaggageUpgradeConfirmation(confirmationData);

                    return;
                }

                this.canGoBackOrForwardToPayment = true;
                window.history.pushState({ ...window.history.state, view: 'payment' }, '', router.currentRoute.fullPath);
                this.moveOnStep('payment');

                
                if (!this.gaAlreadySubmitted.includes('/flights/booking/step3')) {
                    (<any>this).$ga.addFlightProduct(this.reservation.selectedFlight, this.reservation.passengers.length);
                    this.reservation.selectedProducts.forEach(p => {
                        (<any>this).$ga.addProduct(p, this.reservation.passengers.length);
                    });
                    (<any>this).$ga.setAction('checkout', { 'step': 3 });
                    this.gaAlreadySubmitted.push('/flights/booking/step3');

                    utils.TrackingService.track(
                        utils.TrackEvent.BookingStepPayment,
                        this.trackParams
                    );
                }
                break;
            case 'payment':
                if (!this.$v.reservation!.$error) {
                    this.view = 'booking';
                    const model = this.$v.reservation!.$model;
                    if (model.payment.selectedPaymentType.paymentMethods
                        && model.payment.selectedPaymentType.paymentMethods[0]
                        && this.reservation.payment!.selectedPaymentType.isYandexPay) {
                        this.$v.reservation!.$model.paymentData = new YandexPaymentData(
                            model.payment.selectedPaymentType.paymentMethods[0].paymentMethodOptions,
                            this.reservation.getTotalPrice(),
                            this.flightBookingDetails!.bookingSessionId
                        );
                    }
                    const g = utils.clone(this.gaParams);

                    if (!this.gaAlreadySubmitted.includes('flights.booking.step3.submit')) {
                        (<any>this).$ga.sendEvent('flights.booking', 'step3.submit', 'flights.booking.step3.submit', g);
                        this.gaAlreadySubmitted.push('flights.booking.step3.submit');
                    }

                    if (!this.gaAlreadySubmitted.includes('flights.booking.product.confirm.started')) {
                        (<any>this).$ga.sendEvent('flights.booking', 'product.confirm.started', 'flights.booking.product.confirm.started', g);
                        this.gaAlreadySubmitted.push('flights.booking.product.confirm.started');
                    }
                    
                    const processFn = () => {
                        new api.ReservationService().processBeforeBooking(this.searchId, this.flightBookingDetails!, this.reservation).then(
                            () => {
                                const confirmationData = this.reservation.getConfirmationDataForUndonePopupByPosition(api.ProductPopupPosition.afterOrder);
                                
                                if (confirmationData == 'skipPopupAndGo') {
                                    this.onBookingStateSubmit();

                                    return;
                                }

                                //test
                                if(confirmationData?.services) {
                                    this.serveBrandConfirmation(confirmationData);

                                    return;
                                }

                                if (confirmationData?.product && !this.reservation.payment!.selectedPaymentType.isYandexPay) {
                                    this.serveProductConfirmation(confirmationData);

                                    return;
                                }

                                if (confirmationData?.productDiscount && !this.reservation.payment!.selectedPaymentType.isYandexPay) {
                                    this.serveProductDiscountConfirmation(confirmationData);

                                    return;
                                }

                                if (confirmationData?.baggageData && !this.reservation.payment!.selectedPaymentType.isYandexPay) {
                                    this.serveBaggageUpgradeConfirmation(confirmationData);

                                    return;
                                }

                                (<any>this).$ga.setAction('checkout_option', { 'step': 3, 'option': this.reservation.payment!.selectedPaymentTypeId });

                                this.canGoBackOrForwardToPassengers = false;
                                this.canGoBackOrForwardToServices = false;
                                this.canGoBackOrForwardToPayment = false;

                                g.dimension1 = this.reservation.payment!.selectedPaymentTypeId;

                                utils.TrackingService.updateParamsFromReservationBeforeSubmit(this.trackParams, this.reservation);

                                if (!this.gaAlreadySubmitted.includes('/flights/booking/payment')) {
                                    (<any>this).$ga.sendPageview('/flights/booking/payment', 'Flights / Booking / Payment', g);
                                    this.gaAlreadySubmitted.push('/flights/booking/payment');

                                    utils.TrackingService.track(
                                        utils.TrackEvent.BookingPayment,
                                        this.trackParams
                                    );
                                }

                                if(this.reservation.hasSeatMaps()){
                                    Object.values(this.reservation.flatSelectedSeats).forEach((flight: [])=>{
                                        if(flight.length){
                                            (<any>this).$ga.sendEvent('flights.booking', 'seat.selected', 'flights.booking.seat.selected', g);
                                        }
                                    });
                                }

                                if(this.reservation.hasSelectedAnyCustomBaggage){
                                    this.reservation.selectedFlight?.customBaggage.options?.forEach(optionsByDirection=>{
                                        for (const direction of ['Outward', 'Return', 'Both']){
                                            if(optionsByDirection.customBaggageFlightDirection == direction){
                                                for (let i = 0; i < optionsByDirection.baggageOptions.length; i++) {
                                                    if(this.reservation.getSelectedCustomBaggageIds().includes(optionsByDirection.baggageOptions[i].id)){
                                                        (<any>this).$ga.sendEvent('flights.booking', 'baggage.selected', 'flights.booking.baggage.selected', g);
                                                        return;
                                                    }
                                                }
                                            }
                                        }
                                    });
                                }

                                if (this.reservation.brandGroups.length > 0) {
                                    this.reservation.brandGroups.forEach((brandGroup) => {
                                        if (brandGroup.defaultFlightId !== brandGroup.selectedFlightId) {
                                            (<any>this).$ga.sendEvent('flights.booking', 'brands.selected', 'flights.booking.brands.selected', g);

                                            if (brandGroup.brandChangeState) {
                                                (<any>this).$ga.sendEvent('flights.booking', 'brands.selected.' + brandGroup.brandChangeState, 'flights.booking.brands.selected.' + brandGroup.brandChangeState, g);
                                            }
                                        }
                                    });
                                } else {
                                    if (this.reservation.selectedFlightId !== this.flightBookingDetails!.selectedFlightUniqueId) {
                                        (<any>this).$ga.sendEvent('flights.booking', 'brands.selected', 'flights.booking.brands.selected', g);

                                        if (this.brandChangeState) {
                                            (<any>this).$ga.sendEvent('flights.booking', 'brands.selected.' + this.brandChangeState, 'flights.booking.brands.selected.' + this.brandChangeState, g);
                                        }
                                    }
                                }
                                this.onBooking();
                            },
                            (error: any) => {
                                (<any>this).$ga.sendEvent('error', 'ProcessBeforeFlightBooking', 'error.ProcessBeforeFlightBooking', g);
                            }
                        );
                    };

                    if (this.reservation.payment!.selectedPaymentType.isYandexPay) {
                        console.log('create Yandex Pay');
                        
                        new api.ReservationService().yaCreatePayment(model.paymentData).then((payment) => {
                            // Подписаться на событие process.
                            payment.checkout();
                            payment.on((<any>window).YaPay.PaymentEventType.Process, (event) => {
                                console.log('Yandex Pay - success!');
                                (<any>this).$ga.sendPageview(
                                    `/flights/booking/payment/${selectedPayment.kind}/${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '/' + selectedPaymentMethod.type : ''}`.toLowerCase(),
                                    `Flights / Booking / Payment / ${selectedPayment.kind} / ${selectedPayment.paymentTypeId}${selectedPaymentMethod ? ' /' + selectedPaymentMethod.type : ''}`,
                                    this.gaParams
                                );
                                this.reservation.payment!.paymentMethod = {
                                    type: 'YandexPay',
                                    encryptedPacket: event.token
                                };
                                // Закрыть форму Yandex.Pay.
                                payment.complete((<any>window).YaPay.CompleteReason.Success);
                                processFn();
                            });
                            payment.on((<any>window).YaPay.PaymentEventType.Abort, () => {
                                console.log('Yandex Pay - Abort!');
                                this.view = 'form';
                            });
                            payment.on((<any>window).YaPay.PaymentEventType.Error, () => {
                                console.log('Yandex Pay - Error!');
                                (<any>this.$root).$refs.dialogMessage.show({
                                    title: i18next.t('error'),
                                    text: i18next.t('error_payment_yandex_not_available'),
                                    btnOkHandler: () => {
                                        this.view = 'form';
                                    },
                                    btnCloseHandler: () => {
                                        this.view = 'form';
                                    }
                                });
                                payment.complete((<any>window).YaPay.CompleteReason.Error);
                            });
                        }).catch(() => {
                            console.log('Yandex Pay - Server error!');
                            (<any>this).$ga.sendEvent(
                                'flights.booking',
                                `payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.notavailable`.toLowerCase(),
                                `flights.booking.payment.${selectedPayment.kind}.${selectedPayment.paymentTypeId}${selectedPaymentMethod ? '.' + selectedPaymentMethod.type : ''}.notavailable`.toLowerCase(),
                                this.gaParams
                            );

                            (<any>this.$root).$refs.dialogMessage.show({
                                title: i18next.t('error'),
                                text: i18next.t('error_payment_yandex_not_available'),
                                btnOkHandler: () => {
                                    this.view = 'form';
                                },
                                btnCloseHandler: () => {
                                    this.view = 'form';
                                }
                            });
                        });
                    } else {
                        processFn();
                    }
                }
                break;
        }
    }

    showFlightDetails() {
        this.setLastHistoryStateView();
        this.view = 'details';
        this.$nextTick(function () {
            window.scrollTo(0, 0);
        });
    }

    onFlightDetailsClick() {
        (<any>this).$ga.sendEvent('flights.results', 'flight.details', 'flights.results.flight.details', this.gaParams);

        window.history.pushState({ ...window.history.state, view: 'details' }, '', router.currentRoute.fullPath);
        this.showFlightDetails();
    }
    
    showBookingForm() {
        this.setLastHistoryStateView();
        this.view = 'form';
        this.$nextTick(function () {
            window.scrollTo(0, 0);
        });
    }

    onFlightDetailsBackClick() {
        window.history.go(-1);
        switch(this.formStep) {
            case 'passengers':
                (<any>this).$ga.sendPageview('/flights/booking/start', 'Flights / Booking / Start', this.gaParams);
                break;
            case 'services':
                (<any>this).$ga.sendPageview('/flights/booking/step2', 'Flights / Booking / Step2', this.gaParams);
                break;
            case 'payment':
                (<any>this).$ga.sendPageview('/flights/booking/step3', 'Flights / Booking / Step3', this.gaParams);
                break;
        }
    }

    onFlightTerms(flightUniqueId: string) {
        if (this.reservation) {
            (<any>this).$refs.flightTerms.show(this.searchId, flightUniqueId);
        }
    }

    /**
     * Catch first input and notify analytics.
     */
    onPassengersChange() {
        if (!this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
            (<any>this).$ga.sendEvent('flights.booking', 'input.started', 'flights.booking.input.started', this.gaParams);
            (<any>this).$ga.sendPageview('/flights/booking/inputstart', 'Flights / Booking / Inputstart', this.gaParams);

            this.gaAlreadySubmitted.push('flights.booking.input.started');
        }
    }

    get relatedProducts() {
        return this.reservation.getPackageRelatedProductsByExternalId(this.productToConfirm!.product.externalId) || [];
    }

    closePopup() {
        this.onProductConfirm(false);
    }
    onProductConfirm(confirmed: boolean, btnId?: number | string, isLastPopup?: boolean) {
        const product = this.productToConfirm;

        const nextTrick = () => {
            this.$nextTick(() => {
                // Timeout for animations
                window.setTimeout(() => {
                    this.onBookingStateSubmit();
                }, 300);
            });
        };

        let waitTrick = false;

        const discountConfirmisActive = (<any>this.$refs).modalProductDiscountConfirm.isActive;

        if (btnId !== undefined) {
            (<any>this).$ga.sendEvent(
                discountConfirmisActive ? 'product.catchup.popup' : 'product.catchup',
                confirmed ? 'btn.ok' : 'btn.cancel',
                `product.catchup${discountConfirmisActive ? '.popup' : ''}.kit${this.modalConfirmProductContent?.kitId}.id${product?.product.id}`,
                this.gaParams
            );
        }

        if (this.modalProductConfirmTestMode) {
            this.modalProductConfirmTestMode = false;
            (<any>this.$refs).modalProductConfirm.isActive = false;
            (<any>this.$refs).modalProductDiscountConfirm.isActive = false;
            console.log('onProductConfirm ignored (test mode)');
            return;
        }

        if (confirmed && product) {
            const popup = this.reservation.productPopups.find(p => {
                return p.productId === product.product.id;
            });

            product.selected = true;

            if (popup?.position === api.ProductPopupPosition.afterOrder) {
                product.choiceType = isLastPopup ? api.ProductChoiceType.popupForPopupAccepted : api.ProductChoiceType.confirmationAccepted;
            } else {
                product.choiceType = api.ProductChoiceType.popupOnOrderFormAccepted;
            }

            if (this.relatedProducts.length) {
                this.relatedProducts.forEach(relatedProduct => {
                    let productToUnselect: flights.ProductModel | undefined = undefined;

                    if (relatedProduct.criterion == 'equal') {
                        productToUnselect = this.reservation.products.find(product =>
                            product.product.externalId == relatedProduct.externalId
                        );
                    }

                    if (relatedProduct.criterion == 'popup') {
                        productToUnselect = this.reservation.products.find(product =>
                            utils.String.startsWith(product.product.externalId, relatedProduct.externalId)
                        );
                        
                        if (productToUnselect && productToUnselect!.selected) {
                            this.alreadyInPackageModal.message = i18next.t('label_product_already_in_package', <any>{ i: productToUnselect!.product.name });
                            waitTrick = true;
                            this.alreadyInPackageModal.acceptCallback = () => {
                                (<any>this.$refs).alreadyInPackageDialog.isActive = false;
                                productToUnselect!.selected = false;
                                nextTrick();
                            };
                            this.alreadyInPackageModal.refuseCallback = () => {
                                (<any>this.$refs).alreadyInPackageDialog.isActive = false;
                                nextTrick();
                            };
                            (<any>this.$refs).alreadyInPackageDialog.isActive = true;
                        }
                    }
                });
            }
        } else if (product) {
            product.choiceType = api.ProductChoiceType.confirmationDenied;
        }
        
        (<any>this.$refs).modalBrandsPopup.isActive = false;
        (<any>this.$refs).modalProductConfirm.isActive = false;
        (<any>this.$refs).modalProductDiscountConfirm.isActive = false;

        if (!waitTrick) {
            nextTrick();
        }
    }

    showOutOfStockDialog() {
        const dialogParams: any = {
            title: i18next.t('title_flight_out_of_stock'),
            text: this.reservation.flights.length > 1
                ? i18next.t('label_flight_out_of_stock_fare')
                : i18next.t('label_flight_out_of_stock'),
            persistent: true
        };

        let anyFlightToBook = false;

        for (let i = 0; i < this.reservation.flights.length; i++) {
            if (this.reservation.flights[i].bookingPossible) {
                anyFlightToBook = true;
                break;
            }
        }

        if (!anyFlightToBook) {
            dialogParams.btnOkHandler = () => {
                router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams!));
            };

            utils.TrackingService.track(
                utils.TrackEvent.BookingOutOfStock,
                this.trackParams,
                {
                    ErrorReason: 'CheckBooking'
                }
            );
        }

        (<any>this.$root).$refs.dialogMessage.show(dialogParams);
    }
    getProductPackageCheckPromiseCreator(packageExternalId: string, relatedProducts: { externalId: string; criterion: 'equal' | 'startsWith' | 'popup' }[], product: flights.ProductModel, value: boolean) {
        const productPackageToCheck = this.reservation.products.find(p =>
            p.product.externalId == packageExternalId
        );

        const productIsOneOfRelated: boolean = !!relatedProducts.find((rp) => {
            return  ((rp.criterion == 'equal' || rp.criterion == 'popup') && rp.externalId == product.product.externalId)
                    || (rp.criterion == 'startsWith' && utils.String.startsWith(product.product.externalId, rp.externalId));
        });

        if (value && productPackageToCheck && productPackageToCheck.selected && productIsOneOfRelated) {
            
            this.alreadyInPackageModal.message = i18next.t('label_product_already_in_package', <any>{ i: productPackageToCheck.product.name });
            
            this.alreadyInPackageModal.acceptCallback = () => {
                productPackageToCheck.selected = false;
                (<any>this.$refs).alreadyInPackageDialog.isActive = false;
                window.setTimeout(() => {
                    this.finalCheck(false, value, product);
                }, 300); // popup animation
            };
            
            this.alreadyInPackageModal.refuseCallback = () => {
                (<any>this.$refs).alreadyInPackageDialog.isActive = false;
                window.setTimeout(() => {
                    this.finalCheck(true, value, product);
                }, 300); // popup animation
            };
            
            if (product.product.type !== api.ProductType.package) {
                (<any>this.$refs).alreadyInPackageDialog.isActive = true;
            }

        } else {
            this.finalCheck(false, value, product);
        }
    }

    finalCheck(needRollbackValue: boolean, value: boolean, product: flights.ProductModel) {
        
        if (!needRollbackValue) {
            value ? product.choiceType = api.ProductChoiceType.manualAccepted 
                : product.choiceType = api.ProductChoiceType.manualDenied;
        } else {
            product.selected = !value;
        }

        return product;
    }

    onProductSelectionChange(product: flights.ProductModel, value: boolean) {
        // Packages selection change check is handled by package component.
        // So only insurances and serviecs go on here.
        
        if (product.product.type == api.ProductType.insurance
            || product.product.type == api.ProductType.service
            || product.product.type == api.ProductType.package
            || product.product.displayType == api.ProductDisplayType.grouped) {

            for (const productPackageExternlId in this.reservation.packageRelatedProducts) {
                this.getProductPackageCheckPromiseCreator(
                    productPackageExternlId,
                    this.reservation.packageRelatedProducts[productPackageExternlId],
                    product,
                    value
                );
            }
        }
    }    

    onProductDetails(product: flights.ProductModel) {
        this.selectedProduct = product;
        (<any>this.$refs).modalProductDetails.isActive = true;
    }

    onUpgradeBaggageRefuseClick(btnId?: number | string) {
        const cheapestBaggageUpgradeMethod = this.reservation.getCheapestBaggageUpgradeMethod();

        if (cheapestBaggageUpgradeMethod && cheapestBaggageUpgradeMethod.method == 'brand') {
            this.reservation.brandChoiceType = api.FlightBrandChoiceType.confirmationDefault;
        } else if (cheapestBaggageUpgradeMethod && cheapestBaggageUpgradeMethod.method == 'customBaggage') {
            this.reservation.baggageChoiceType = api.BaggageChoiceType.confirmationDenied;
        }
        (<any>this.$refs).modalBaggageUpgrade.isActive = false;
        this.onBookingStateSubmit();
        (<any>this).$ga.sendEvent('flights.booking',
            `id.btn${btnId}.product.confirm.back.kit${this.modalUpgradeBaggageContent?.kitId}.id${this.modalUpgradeBaggageContent?.productId}`,
            `flights.booking.id.btn${btnId}.product.confirm.back.kit${this.modalUpgradeBaggageContent?.kitId}.id${this.modalUpgradeBaggageContent?.productId}`,
            this.gaParams);
    }

    onUpgradeBaggageBuyClick(btnId?: number | string) {
        const cheapestBaggageUpgradeMethod = this.reservation.getCheapestBaggageUpgradeMethod();

        if (cheapestBaggageUpgradeMethod && cheapestBaggageUpgradeMethod.method == 'brand') {
            this.reservation.brandChoiceType = api.FlightBrandChoiceType.confirmationUpgrade;
        } else if (cheapestBaggageUpgradeMethod && cheapestBaggageUpgradeMethod.method == 'customBaggage') {
            this.reservation.baggageChoiceType = api.BaggageChoiceType.confirmationAccepted;
        }
        (<any>this.$refs).modalBaggageUpgrade.isActive = false;
        this.reservation.upgradeBaggage();
        this.onBookingStateSubmit();
        // (<any>this).$ga.sendEvent('flights.booking', 'btn.baggage.confirm.ok', 'flights.booking.btn.baggage.confirm.ok', this.gaParams);
        (<any>this).$ga.sendEvent('flights.booking',
            `id.btn${btnId}.product.confirm.back.kit${this.modalUpgradeBaggageContent?.kitId}.id${this.modalUpgradeBaggageContent?.productId}`,
            `flights.booking.id.btn${btnId}.product.confirm.back.kit${this.modalUpgradeBaggageContent?.kitId}.id${this.modalUpgradeBaggageContent?.productId}`,
            this.gaParams);
    }

    onServiceBrandChange(product: api.Product, prevProduct: api.Product) {
        const productModel =  this.reservation.products.find(item => item.product.id === product.productId);

        if (productModel) {
            new api.RetailRocket().productSelected({
                flightKey: api.RetailRocketCL.flightKey,
                productId: productModel.product.id
            }).catch(err => {
                console.log(err);
            });

            productModel.choiceType = api.ProductChoiceType.manualAccepted;
        }
    }

    onServiceBrandChangeInPopup(product: api.Product, prevProduct: api.Product) {
        const productModel =  this.reservation.products.find(item => item.product.id === product.productId);

        if (productModel) {
            new api.RetailRocket().productSelected({
                flightKey: api.RetailRocketCL.flightKey,
                productId: productModel.product.id
            }).catch(err => {
                console.log(err);
            });

            productModel.choiceType = api.ProductChoiceType.confirmationAccepted;
        }
    }

    onServiceBrandConfirmInPopup() {
        this.onProductConfirm(true);
    }

    moveOnStep(step: FormStep) {
        this.formStep = step;
        this.setLastHistoryStateView();
    }

    onPassengersStepClick() {
        if (this.formStep == 'passengers' || !this.canGoBackOrForwardToPassengers) {
            return;
        }
        window.history.pushState({ ...window.history.state, view: 'passengers' }, '', router.currentRoute.fullPath);
        this.moveOnStep('passengers');
    }

    onSeatsStepClick() {
        if (this.formStep == 'seats' || !this.canGoBackOrForwardToSeats) {
            return;
        }
        window.history.pushState({ ...window.history.state, view: 'seats' }, '', router.currentRoute.fullPath);
        this.moveOnStep('seats');
    }

    onServicesStepClick() {
        if (this.formStep == 'services' || !this.canGoBackOrForwardToServices) {
            return;
        }
        window.history.pushState({ ...window.history.state, view: 'services' }, '', router.currentRoute.fullPath);
        this.moveOnStep('services');
    }

    onPaymentStepClick() {
        if (this.formStep == 'payment' || !this.canGoBackOrForwardToPayment) {
            return;
        }
        window.history.pushState({ ...window.history.state, view: 'payment' }, '', router.currentRoute.fullPath);
        this.moveOnStep('payment');
    }

    onPaymentWithService() {
        const reservationService = new api.ReservationService();
        this.disabledServiceButtons = true;

        (<any>this).$ga.sendEvent(
            'payment',
            'error',
            'payment.error.limit.card.ok.product',
            this.gaParams
        );

        reservationService
            .getReservationAndBillStatus(this.reservationInfo!.reservationPublicId, this.paymentInfo!.billPublicId, false)
            .then(
                (result: api.GetReservationStatusResult) => {
                    location.href = '/order/payment/' + result.reservationInfo!.reservationPublicId;
                },
                () => {
                    location.href = '/order/payment/' + this.reservationInfo!.reservationPublicId;
                }
            );
    }
    brandAccum(index, FlyId){
        // if(this.reservation.products){
        //     this.reservation.products.forEach(item => {
        //         if(item.selected == true && item.product.flightId !== FlyId) {
        //             item.product.flightId !== null ? item.selected = false : null;
        //             this.reservation.products.forEach(products => {
        //                 if(products.product.id == item.product.id && products.product.flightId == FlyId && products.product.saleType !== 'YandexPayButton'){
        //                     products.selected = true;
        //                 }
        //             });
        //         }
        //     });
        // }
    }
    onPaymentDeleteService() {
        const reservationService = new api.ReservationService();
        this.disabledServiceButtons = true;

        (<any>this).$ga.sendEvent(
            'payment',
            'error',
            'payment.error.limit.card.delete.product',
            this.gaParams
        );

        reservationService
            .getReservationAndBillStatus(this.reservationInfo!.reservationPublicId, this.paymentInfo!.billPublicId, true)
            .then(
                (result: api.GetReservationStatusResult) => {
                    location.href = '/order/payment/' + result.reservationInfo!.reservationPublicId;
                },
                () => {
                    location.href = '/order/payment/' + this.reservationInfo!.reservationPublicId;
                }
            );
    }

    tryToRestoreReservation() {
        const reservationService = new api.ReservationService();

        this.view = 'loading';
        reservationService
            .getReservationByBookingSession(this.flightBookingDetails!.bookingSessionId)
            .then(
                (result) => {
                    if (result && uuidValidate(result)) {
                        this.$router.push(`/order/${result}`);
                    }
                },
                (error) => {
                    if (error.response) {
                        console.log('api/GetReservationByBookingSession XHR failed', error.response.status, error.response.data);
                    } else {
                        console.log('api/GetReservationByBookingSession XHR error', error.message);
                    }

                    if (!error.response && error.message == 'Network Error') {
                        // Network error.
                        this.view = 'network-error';

                        return;
                    }

                    this.onBooking();
                }
            );
    }

    getCloseIconVisible() {
        let result = false;

        for (const product of this.brandContent.products) {
            const p = this.reservation.productPopups.filter(popup => popup.displayType === 'Grouped').find(popup => popup.productId === product.productId);

            if (p?.isCloseIconVisible) {
                result = true;
                break;
            }
        }

        return result;
    }

    // stop polling API if the user has closed the SBP dialog
    handleDialogSbpQrClosing() {
        ReservationService.needToStopSbpPolling = true;
    }

    hasFlightService(passengers: flights.PassengerModel[]) {
        let has = false;

        for (const passenger of passengers) {
            for (const option of passenger.baggageOptions) {
                if (option !== this.NO_BAGGAGE_GUID) {
                    has = true;
                    break;
                }
            }

            if (Object.keys(passenger.selectedFlightSeats).length > 0) {
                has = true;
            }

            if (has) {
                break;
            }
        }

        return has;
    }
}
